import { useEffect, useState } from "react";
import {
  Box,
  Tooltip,
  Divider,
  TextField,
  InputAdornment,
  List,
  ListItem,
  ListItemIcon,
  ListItemText,
  Checkbox,
  IconButton,
  Snackbar,
  Typography,
  Stack,
  Popover,
  Fade,
} from "@mui/material";
import SearchIcon from "@mui/icons-material/Search";
import VisibilityIcon from "@mui/icons-material/Visibility";
import EditIcon from "@mui/icons-material/Edit";
import AddBoxIcon from "@mui/icons-material/AddBox";
import DeleteIcon from "@mui/icons-material/Delete";
import CheckIcon from "@mui/icons-material/Check";
import CloseIcon from "@mui/icons-material/Close";
import DataService from "../../services/getData";
import MuiAlert from "@mui/material/Alert";
import MapUtils from "../../utils/mapUtils";

export default function ProjectSelection({ onDataLoaded }) {
  const [listOfProjects, setListOfProjects] = useState([]);
  const [search, setSearch] = useState("");
  const [checked, setChecked] = useState([]);
  const [loading, setLoading] = useState(true);
  const [SnackbarValue, setSnackbarValue] = useState({
    severity: "warning",
    message: "",
  });
  const [openSnackbar, setOpenSnackbar] = useState(false);
  const [isError, setIsError] = useState(false);
  const [editingProjectId, setEditingProjectId] = useState(null);
  const [newName, setNewName] = useState("");

  const [anchorEl, setAnchorEl] = useState(null);
  const [currentProject, setCurrentProject] = useState(null);
  const [indicatorActions, setIndicatorActions] = useState([{}]);
  const [allActions, setAllActions] = useState([]);

  const getAllActions = async () =>{
    const dataTemp2 = await DataService.GetActions();
    //console.log("Actions=", dataTemp2.data);
    setAllActions(dataTemp2.data || []);
    const uniqueIndicators = [
      ...new window.Map((dataTemp2.data || []).map((item) => [item.indicatorName, item])).values(),
    ];
    //console.log("Unique Indicators=", uniqueIndicators);
    setIndicatorActions(uniqueIndicators);
  };

  // The exact action saved with the project; projects saved before actionSimulationId was stored
  // correctly fall back to any action of the same indicator.
  const findSavedAction = (a) =>
    allActions.find((it) => a.actionSimulationId && it.actionSimulationId === a.actionSimulationId) ||
    indicatorActions.find((it) => it.indicatorId === a.indicatorId);

  // Only an exact match identifies the action; the indicator fallback is an arbitrary action of that indicator.
  const savedActionName = (a, action) =>
    action && a.actionSimulationId && action.actionSimulationId === a.actionSimulationId
      ? (action.actionSimulationName || "").trim().replace(/\.$/, "")
      : "";

  const levelLabel = (obj, levelValue) => {
    const value = String(levelValue ?? "").trim();
    if (!obj) return value;
    for (let i = 1; i <= 3; i++) {
        const itemValue = obj[`listItem${i}Value`];
        if (itemValue != null && String(itemValue) === value) {
          return obj[`listItem${i}Name`];
        }
      }
    return value;
  };

  useEffect(() => {
    const getAllProjects = async () => {
      try {
        const dataTemp = await DataService.GetUserProjects();
        const sortedProjects = dataTemp.data.reverse(
          (a, b) => a.processNumber - b.processNumber
        );
        setListOfProjects(sortedProjects);
        if (onDataLoaded) {
          onDataLoaded({ projects: sortedProjects, checkedProjects: checked });
        }
      } catch (error) {
        console.error("Erro ao obter projetos:", error);
        if (error.response && error.response.status === 401) {
          // workaround to force logout
          document.querySelector("#btn-login button").click();
          return;
        }

        setListOfProjects({ projects: [], checkedProjects: [] });

        if (onDataLoaded) {
          onDataLoaded({ projects: [], checkedProjects: [] }); // Em caso de erro, envia lista vazia
        }
      } finally {
        setLoading(false);
      }
    };

    getAllActions();
    getAllProjects();
  }, []);

  useEffect(() => {
    if (listOfProjects.length > 0) {
      onDataLoaded({ projects: listOfProjects, checkedProjects: checked });
    }
  }, [checked, listOfProjects, onDataLoaded]);

  useEffect(() => {
    if (!(SnackbarValue.message == "")) {
      setOpenSnackbar(true);
    } else {
      setOpenSnackbar(false);
    }
  }, [SnackbarValue]);

  useEffect(() => {
    console.log("Current Project=", currentProject);
  }, [currentProject]);

  //Popover stuff//
  const handlePopoverOpen = (event, project) => {
    //console.log("Project=", project.projectNumber);
    setAnchorEl(event.currentTarget);
    setCurrentProject(project);
  };

  const handlePopoverClose = () => {
    setAnchorEl(null);
    setCurrentProject(null);
  };

  const open = Boolean(anchorEl);
  // End Popover//

  const handleEditClick = (project) => {
    setEditingProjectId(project.projectNumber);
    setNewName(project.projectName);
  };

  const handleViewProject = (project) => {
    handleToggle(project);
  };

  const handleCancelEdit = () => {
    setEditingProjectId(null);
    setNewName("");
  };

  const handleSaveEdit = async (projectNumber, newName) => {
    try {
      const response = await DataService.UpdateUserProject(
        projectNumber,
        newName
      );

      // Atualiza lista localmente
      const updatedProjects = filteredProjects.map((p) =>
        p.projectNumber === projectNumber ? { ...p, projectName: newName } : p
      );
      setListOfProjects(updatedProjects); // ✅ Atualiza estado para re-render

      //console.log("Projeto atualizado:", updatedProjects);
      setEditingProjectId(null);
      setNewName("");
    } catch (error) {
      console.error("Erro ao atualizar projeto:", error);
    }
  };

  const handleDuplicate = async (projectNumber) => {
    try {
      console.log("A duplicar o Project Number=", projectNumber);
      const response = await DataService.DuplicateUserProject(projectNumber);

      if (!response.data.projectNumber) {
        throw new Error("Erro ao duplicar projeto");
      }
      setSnackbarValue({
        severity: "success",
        message: "You have successfully created a copy the project",
      });
      setListOfProjects((prevProjects) => [response.data, ...prevProjects]);
    } catch (error) {
      setSnackbarValue({
        severity: "error",
        message: "An error occurred while duplicating the project",
      });
      console.error(error);
    }
  };

  const handleDelete = async (projectNumber) => {
    try {
      console.log("A eliminar o Project Number=", projectNumber);
      const response = await DataService.DeleteUserProject(projectNumber);

      if (!response.statusText === "OK") {
        throw new Error("Erro ao apagar projeto");
      }
      setSnackbarValue({
        severity: "success",
        message: "You have successfully deleted the project",
      });
      setListOfProjects(
        listOfProjects.filter(
          (project) => project.projectNumber !== projectNumber
        )
      );
    } catch (error) {
      setSnackbarValue({
        severity: "error",
        message: "An error occurred while deleting the project",
      });
      console.error(error);
    }
  };

  const handleClose = () =>
    setSnackbarValue({
      severity: "warning",
      message: "",
    });

  const handleToggle = (project) => {
    //const currentIndex = checked.indexOf(project);
    //const newChecked = [...checked];
    const currentIndex = checked.findIndex(
      (p) => p.projectNumber === project.projectNumber
    );
    const newChecked = [...checked];

    if (currentIndex === -1) {
      if (newChecked.length === 3) {
          setSnackbarValue({
            severity: "warning",
            message: "You cannot compare more than 3 projects!",
          });
          return;
      }

      project.projectInfo.actions?.map((a) => {
        const action = findSavedAction(a);

        a.indicatorName = action ? action.indicatorName : "Unknown Indicator";
        a.actionName = savedActionName(a, action);
        a.level = levelLabel(action, a.levelValue);
      });  

      newChecked.push(project);
    } else {
      newChecked.splice(currentIndex, 1);
    }
    console.log("Checked=", newChecked);
    setChecked(newChecked);
  };

  const filteredProjects = listOfProjects.filter((p) =>
    p.projectName.toLowerCase().includes(search.toLowerCase())
  );

  if (loading) {
    return <Box sx={{ p: 2 }}>Loading...</Box>;
  }

  if (!listOfProjects || listOfProjects.length === 0) {
    return null; // não renderiza nada se não houver dados
  }

  return (
    <Box sx={{ width: "100%", maxWidth: 600, minWidth: 300 }}>
      {/* Campo de pesquisa */}
      <TextField
        fullWidth
        placeholder="Search for a project"
        value={search}
        onChange={(e) => setSearch(e.target.value)}
        InputProps={{
          endAdornment: (
            <InputAdornment position="start">
              <SearchIcon />
            </InputAdornment>
          ),
        }}
      />

      {/* Lista com scroll */}
      <Box sx={{ maxHeight: 400, overflowY: "auto", overflowX: "hidden" }}>
        <List dense>
          {filteredProjects.map((project) => (
            <ListItem
              key={project.projectNumber}
              secondaryAction={
                <>
                  <IconButton
                    edge="end"
                    aria-label="view"
                    onMouseEnter={(e) => handlePopoverOpen(e, project)}
                    onMouseLeave={handlePopoverClose}>
                    <VisibilityIcon color="primary-color" />
                  </IconButton>

                  <Popover
                    anchorEl={anchorEl}
                    open={open}
                    onClose={handlePopoverClose}
                    transitionComponent={Fade} // Fade effect
                    transitionDuration={300} // duração do fade
                    anchorOrigin={{
                      vertical: "bottom",
                      horizontal: "left",
                    }}
                    transformOrigin={{
                      vertical: "top",
                      horizontal: "left",
                    }}
                    disableRestoreFocus
                    sx={{ pointerEvents: "none"}} // Permite que o popover não interfira com outros elementos
                    slotProps={{
                      paper: {
                        sx: {
                          minWidth: 0, // evita encolher
                          //boxShadow: "0px 2px 4px rgba(220, 220, 220, 0.05)",
                          border: "1px solid #e0e0e0",
                          boxShadow: "none",
                          borderRadius: 2,
                          padding: 0,
                          maxWidth: "calc(100vw - 40px)",
                          overflowX: "hidden",
                          whiteSpace: "normal",
                          overflowWrap: "break-word",
                          wordBreak: "break-word"
                        },
                      },
                    }}>
                    {currentProject && (
                      <Box sx={{ p: 2, minWidth: 150 }}>
                        <Stack>
                          <Typography
                            variant="subtitle2"
                            sx={{ fontWeight: "bold" }}>
                            Location
                          </Typography>
                          <Typography variant="body2">
                            {currentProject?.projectInfo.cityName ?? "-"}
                          </Typography>
                          
                          <Box sx={{ mt: 1 }}>
                            <Typography
                              variant="subtitle2"
                              sx={{ fontWeight: "bold" }}>
                              Actions
                            </Typography>
                            
                            {Array.isArray(currentProject?.projectInfo?.actions) && currentProject.projectInfo.actions.length > 0 ? (                                                      
                            
                              currentProject.projectInfo.actions.map((a, idx) => {                                  
                                const action = findSavedAction(a);

                                const actionName = savedActionName(a, action);
                                const text = (action ? action.indicatorName : "Unknown Indicator") + (actionName ? `: ${actionName}` : "");
                                const level = levelLabel(action, a.levelValue);
                                
                                return (
                                  <Typography key={idx} variant="body2">
                                    {text}{' '}
                                    <Typography component="span" variant="body2" sx={{ color: '#9e9e9e'}} >
                                      ({level})
                                    </Typography>
                                  </Typography>
                                )
                              })
                            ) : (
                              <Typography variant="body2">
                                No actions defined
                              </Typography>
                            )}

                          </Box>
                        </Stack>
                      </Box>
                    )}
                  </Popover>

                  <Tooltip title="Edit">
                    <IconButton
                      edge="end"
                      aria-label="edit"
                      onClick={() => handleEditClick(project)}>
                      <EditIcon color="primary" />
                    </IconButton>
                  </Tooltip>

                  <Tooltip title="Duplicate">
                    <IconButton
                      edge="end"
                      aria-label="add"
                      onClick={() => handleDuplicate(project.projectNumber)}>
                      <AddBoxIcon color="primary" />
                    </IconButton>
                  </Tooltip>

                  <Tooltip title="Delete">
                    <IconButton
                      edge="end"
                      aria-label="delete"
                      onClick={() => handleDelete(project.projectNumber)}>
                      <DeleteIcon color="primary" />
                    </IconButton>
                  </Tooltip>
                </>
              }>
              <ListItemIcon sx={{ minWidth: 32 }}>
                <Checkbox
                  edge="start"
                  checked={
                    checked.findIndex(
                      (p) => p.projectNumber === project.projectNumber
                    ) !== -1
                  }
                  onChange={() => handleToggle(project, indicatorActions)}
                />
              </ListItemIcon>

              {editingProjectId === project.projectNumber ? (
                <Box
                  sx={{
                    position: "absolute", // ou "fixed" se quiser cobrir toda a tela
                    top: 0,
                    left: 0,
                    width: "100%",
                    backgroundColor: "rgba(255,255,255,0.95)", // fundo semi-opaco
                    zIndex: 9999, // garante que fica acima
                    display: "flex",
                    alignItems: "center",
                    gap: 1,
                    padding: "8px",
                  }}>
                  <TextField
                    value={newName}
                    onChange={(e) => setNewName(e.target.value)}
                    size="small"
                    variant="outlined"
                    sx={{ flexGrow: 1 }}
                  />
                  <IconButton
                    aria-label="save"
                    color="primary"
                    onClick={() =>
                      handleSaveEdit(project.projectNumber, newName)
                    }
                    sx={{
                      backgroundColor: "#fff",
                      borderRadius: "50%",
                      boxShadow: "0 1px 3px rgba(0,0,0,0.2)",
                    }}>
                    <CheckIcon />
                  </IconButton>
                  <IconButton
                    aria-label="cancel"
                    color="error"
                    onClick={handleCancelEdit}
                    sx={{
                      backgroundColor: "#fff",
                      borderRadius: "50%",
                      boxShadow: "0 1px 3px rgba(0,0,0,0.2)",
                    }}>
                    <CloseIcon />
                  </IconButton>
                </Box>
              ) : (
                <ListItemText primary={project.projectName} />
              )}
            </ListItem>
          ))}
        </List>
      </Box>

      <Divider sx={{ marginTop: 4 }} />

      <Snackbar
        open={openSnackbar}
        autoHideDuration={3500}
        onClose={handleClose}>
        <MuiAlert severity={SnackbarValue.severity}>
          {SnackbarValue.message}
        </MuiAlert>
      </Snackbar>
    </Box>
  );
}
